import { Spacing } from '@persona-id/design-tokens';
import { sortBy } from 'lodash/fp';
import { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import { Field, FormSection, formValueSelector } from 'redux-form';
import styled from 'styled-components';

import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import MultiSelectField from 'dashboard/components/FormControls/MultiSelectField';
import { OptionType } from 'dashboard/components/ReactSelect';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { RootState } from 'dashboard/redux/store';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label } from 'design-system/components/Text';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import { getOnlyLastPath } from 'lib/utils/url';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;
const selector = formValueSelector(form);

const COUNTRY_OPTIONS: OptionType<CountryCode>[] = Object.keys(COUNTRIES).map(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (options: CountryCode) => ({
    value: options,
    label: COUNTRIES[options],
  }),
);

const SORTED_COUNTRY_OPTIONS: OptionType<CountryCode>[] =
  sortBy<OptionType<CountryCode>>('label')(COUNTRY_OPTIONS);

const DefaultCountryRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: ${Spacing[3]};
`;

const DefaultCountryText = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const EditLink = styled(Link)`
  margin-left: 6px;
`;

const CountriesContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]} ${Spacing[1]};
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: wrap;
  max-height: 200px;
  overflow-y: scroll;
`;

interface FormSectionCountryConfigProps {
  name: string;
  title?: string;
  fieldPath: string;
  selectedCountries: CountryCode[];
}

const FormSectionCountryConfig = ({
  name,
  title = 'Country options',
  fieldPath,
  selectedCountries,
}: FormSectionCountryConfigProps) => {
  const editableUngated = useIsEditableWithOptions();
  // lock country select and country/ID configs from no-product feature orgs - enable for inquiries.free + above
  const editableForFreeTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesFree,
  });

  const selectedCountriesSet = useMemo<Set<CountryCode>>(
    () => new Set(selectedCountries),
    [selectedCountries],
  );

  const fieldSchema = useSelector<RootState, FieldSchema[]>(
    (state) => selector(state, 'field-schema') ?? [],
  );

  const defaultCountry = fieldSchema.find((field) => field.key === getOnlyLastPath(fieldPath))?.[
    'default-value'
  ];

  const templateFilterLabelTextMapper = useLabelTextMapper(COUNTRY_OPTIONS, 'Countries', {
    emptyIsAll: false,
  });

  return (
    <FormSection name={name} component={Fragment}>
      {title && (
        <Label size='md' weight='bold'>
          Country options
        </Label>
      )}
      <FieldWrapper>
        <Field
          size='md'
          id='country-codes'
          name='country-codes'
          data-test='form-section__country-codes'
          component={MultiSelectField}
          labelTextMapper={templateFilterLabelTextMapper}
          disabled={!editableForFreeTier}
          selectOptionValues={SORTED_COUNTRY_OPTIONS}
          selectAllEnabled
          searchEnabled
        />
      </FieldWrapper>
      <Spacer height={1} />
      {selectedCountries.length !== 0 && (
        <>
          <Label size='sm'>{`Added countries (${selectedCountries.length})`}</Label>
          <CountriesContainer data-test='country-codes'>
            {SORTED_COUNTRY_OPTIONS.filter(({ value }) => selectedCountriesSet.has(value)).map(
              ({ label, value }) => (
                <Sticker key={value} variant='primary' data-test={`country-codes--${value}`}>
                  {label}
                </Sticker>
              ),
            )}
          </CountriesContainer>
          <DefaultCountryRow>
            <DefaultCountryText>
              {`Default: ${defaultCountry ?? 'None'}`}
              {editableUngated && <EditLink to={fieldPath}>Update</EditLink>}
            </DefaultCountryText>
          </DefaultCountryRow>
        </>
      )}
    </FormSection>
  );
};

export default FormSectionCountryConfig;
